import React, { useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import { api } from '../../api';

const PrivateWorkshopField = ({ label, name, value, onChange, type = 'text', required: req = false, placeholder = '', multiline = false }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {req && <span className="text-red-500">*</span>}
    </label>
    {multiline ? (
      <textarea
        value={value} onChange={e => onChange(name, e.target.value)}
        rows={3} placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent resize-none"
      />
    ) : (
      <input
        type={type} value={value} onChange={e => onChange(name, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent"
      />
    )}
  </div>
);

export const PrivateWorkshopRequestModal = ({ onClose, user }) => {
  const WORKSHOP_LANGUAGES = [
    "English", "Korean", "Japanese", "Portuguese", "Spanish",
    "German", "French", "Chinese (Traditional)", "Chinese (Simplified)", "Italian",
  ];
  const [form, setForm] = useState({
    course_of_interest: '',
    catalog_url: '',
    expected_attendance: '',
    learner_profile: '',
    expectations: '',
    customizations: '',
    preferred_timeframe: '',
    ms_enablement_lead_email: '',
    preferred_language: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleFieldChange = (name, value) => setForm(f => ({ ...f, [name]: value }));

  const required = ['course_of_interest', 'expected_attendance', 'learner_profile', 'expectations', 'preferred_timeframe', 'preferred_language'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    for (const f of required) {
      if (!form[f]) { setError('Please fill in all required fields.'); return; }
    }
    setSubmitting(true); setError('');
    try {
      await api.request('/private-workshop-requests', {
        method: 'POST',
        body: JSON.stringify({ ...form, expected_attendance: parseInt(form.expected_attendance) || 0 }),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-500" />
          </div>
          <h2 className="text-xl font-bold text-levelup-dark mb-2">Request Submitted!</h2>
          <p className="text-gray-600 mb-6">Your private workshop request has been received. Our team will review it and get back to you within 2-3 business days.</p>
          <button onClick={onClose} className="w-full px-6 py-3 bg-levelup-dark text-white rounded-xl font-semibold hover:bg-levelup-teal transition-colors">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="private-workshop-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-levelup-dark text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold">Request a Private Workshop</h2>
            <p className="text-xs text-gray-300 mt-0.5">From the LevelUp catalog at <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noopener noreferrer" className="text-levelup-accent underline">catalog.skillupwithlevelup.com</a></p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-white"><X size={22} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="bg-levelup-light border border-levelup-teal/20 rounded-lg p-3 text-sm text-levelup-dark">
            Browse our full course catalog at <a href="https://catalog.skillupwithlevelup.com" target="_blank" rel="noopener noreferrer" className="text-levelup-teal font-semibold underline">catalog.skillupwithlevelup.com</a>, then use this form to request a private, customised delivery for your organization.
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <PrivateWorkshopField label="Course / Topic of Interest" name="course_of_interest" value={form.course_of_interest} onChange={handleFieldChange} req placeholder="e.g., M365 Copilot Fundamentals, Azure AI Foundry" />
            </div>
            <div className="md:col-span-2">
              <PrivateWorkshopField label="Catalog Course URL (optional)" name="catalog_url" value={form.catalog_url} onChange={handleFieldChange} placeholder="https://catalog.skillupwithlevelup.com/..." />
            </div>
            <PrivateWorkshopField label="Expected Attendance" name="expected_attendance" value={form.expected_attendance} onChange={handleFieldChange} type="number" req placeholder="e.g., 25" />
            <PrivateWorkshopField label="Preferred Timeframe" name="preferred_timeframe" value={form.preferred_timeframe} onChange={handleFieldChange} req placeholder="e.g., May 2026, Q2 2026, Within 3 months" />
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Workshop Language <span className="text-red-500">*</span>
              </label>
              <select
                value={form.preferred_language}
                onChange={(e) => handleFieldChange('preferred_language', e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-levelup-accent focus:border-transparent bg-white"
                data-testid="workshop-language-select"
                required
              >
                <option value="">Select preferred language</option>
                {WORKSHOP_LANGUAGES.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>
          </div>

          <PrivateWorkshopField label="Learner Profile" name="learner_profile" value={form.learner_profile} onChange={handleFieldChange} req multiline
            placeholder="Who will be attending? Describe their job roles, technical level, industry, and any background knowledge relevant to the course." />

          <PrivateWorkshopField label="Workshop Expectations" name="expectations" value={form.expectations} onChange={handleFieldChange} req multiline
            placeholder="What outcomes are you hoping to achieve? What should learners be able to do after the workshop?" />

          <PrivateWorkshopField label="Customisations Required (optional)" name="customizations" value={form.customizations} onChange={handleFieldChange} multiline
            placeholder="Any specific topics, demos, case studies, or exercises you need adapted? Any language, region, or audience-specific requirements?" />

          <PrivateWorkshopField label="Microsoft Partner Enablement Lead Email (optional)" name="ms_enablement_lead_email" value={form.ms_enablement_lead_email} onChange={handleFieldChange} type="email"
            placeholder="partner-lead@microsoft.com" />

          {error && <p className="text-red-500 text-sm bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="private-workshop-submit"
              className="flex-1 px-4 py-3 bg-levelup-dark text-white rounded-xl font-semibold hover:bg-levelup-teal transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {submitting ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Submitting...</> : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};




// ──────────────────────────────────────────────────────────────────────────────
// EmailSettings extracted to pages/EmailSettings.jsx
// WorkshopPlanner extracted to pages/WorkshopPlanner.jsx
// AccessControl extracted to pages/AccessControl.jsx
// ──────────────────────────────────────────────────────────────────────────────
